import { Button, ButtonProps, Icon } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { LuCheck, LuPlus } from 'react-icons/lu'

export type SelectableButtonsOption = {
  title?: string
  key: string
  label?: string
  children?: React.ReactNode
  selectableButtonProps?: Partial<SelectableButtonProps>
  [key: string]: any
}

export type SelectableButtonsProps = {
  defaultEmptyOption?: boolean | SelectableButtonsOption
  defaultValue?: string[]
  maxSelections?: number
  onChange?: (selections: any) => any
  onBlur?: () => any
  options?: Array<SelectableButtonsOption>
  selectableButtonProps?: Partial<SelectableButtonProps>
  selectedKeys?: string[]
  value?: string[]
}

/**
 * Order-sensitive equality for selection key lists.
 *
 * Used to decide whether a controlled `value` actually changed. Order matters:
 * consumers render chips in selection order, so a reorder is a real change.
 */
export const sameSelectionKeys = (a?: string[] | null, b?: string[] | null): boolean => {
  if (a === b) {
    return true
  }
  if (!a || !b || a.length !== b.length) {
    return false
  }
  return a.every((key, index) => key === b[index])
}

export const SelectableButtons: React.FC<SelectableButtonsProps> = props => {
  const { onChange, options: optionsFromProps, value } = props
  const [selectedKeys, setSelectedKeys] = useState<string[]>(value || props.defaultValue || [])

  // Keep the latest onChange/options without making them effect deps. Consumers commonly pass an
  // inline onChange and a freshly-mapped options array on every render, so including them here would
  // re-fire onChange every render — and when onChange triggers a parent state update, that becomes an
  // infinite render loop. We only want to notify the consumer when the selection actually changes.
  const onChangeRef = useRef(onChange)
  const optionsRef = useRef(optionsFromProps)
  onChangeRef.current = onChange
  optionsRef.current = optionsFromProps

  useEffect(() => {
    onChangeRef.current?.(optionsRef.current?.filter(option => selectedKeys?.includes(option.key)) || [])
  }, [selectedKeys])

  // Sync from a controlled `value`, but only when the *contents* differ.
  //
  // Consumers routinely build this array inline (`value={[answers[key]]}`,
  // `value={selected.filter(...)}`), so a fresh identity arrives on every render and
  // this effect re-runs each time. Assigning unconditionally then re-fired the
  // selection effect above → `onChange` → consumer setState → render → repeat, i.e.
  // "Maximum update depth exceeded" the moment a controlled consumer stored the
  // result. Returning `prev` unchanged lets React bail out and breaks that cycle.
  useEffect(() => {
    if (!Array.isArray(value)) {
      return
    }
    setSelectedKeys(prev => (sameSelectionKeys(prev, value) ? prev : value))
  }, [value])

  const options: SelectableButtonsProps['options'] = props.defaultEmptyOption
    ? [
        props.defaultEmptyOption === true
          ? {
              title: 'Any',
              key: 'any',
            }
          : props.defaultEmptyOption,
        ...props.options,
      ]
    : props.options

  return (
    <>
      {options.map((option, index) => (
        <SelectableButton
          key={index}
          title={option.title}
          value={option.key}
          isSelected={
            selectedKeys?.includes(option.key) ||
            (index == 0 && props.defaultEmptyOption && selectedKeys.length == 0)
          }
          onToggle={key => {
            if (index == 0 && props.defaultEmptyOption) {
              setSelectedKeys([])
              return
            }
            if (selectedKeys?.includes(key)) {
              setSelectedKeys(prev => prev.filter(x => x !== key))
            } else {
              if (props.maxSelections && selectedKeys.length >= props.maxSelections) {
                setSelectedKeys(prev => [...prev.slice(0, props.maxSelections - 1), key])
              } else {
                setSelectedKeys(prev => [...prev, key])
              }
            }
          }}
          {...props.selectableButtonProps}
          {...option.selectableButtonProps}
        >
          {option.children}
        </SelectableButton>
      ))}
    </>
  )
}

export type SelectableButtonProps = {
  activeColor?: ButtonProps['bgColor']
  inactiveColor?: ButtonProps['bgColor']
  buttonProps?: ButtonProps
  children?: React.ReactNode
  iconPlacement?: 'left' | 'top' | 'bottom' | 'right'
  icons?: {
    default: any
    selected: any
  }
  isSelected: boolean
  onToggle: (value: any) => void
  title?: string
  value: any
}

export const SelectableButton: React.FC<SelectableButtonProps> = props => {
  const activeColor = props.activeColor || 'colorPalette.emphasized'
  const inactiveColor = props.inactiveColor || 'colorPalette.emphasized/50'

  return (
    <Button
      {...(props.isSelected
        ? {
            bgColor: activeColor,
            _hover: {
              borderColor: 'colorPalette.border',
              bgColor: activeColor,
            },
          }
        : {
            bgColor: inactiveColor,
            _hover: {
              bgColor: inactiveColor,
              borderColor: activeColor,
            },
          })}
      borderRadius={'md'}
      color={props.isSelected ? 'colorPalette.contrast' : 'colorPalette.fg'}
      flexDirection={props.iconPlacement === 'top' || props.iconPlacement === 'bottom' ? 'column' : 'row'}
      gap={'0.5em'}
      height={'auto'}
      paddingX={'1em'}
      size={'sm'}
      variant={'outlineGhost'}
      onClick={() => {
        props.onToggle(props.value)
      }}
      {...props.buttonProps}
    >
      {props.icons && props.iconPlacement === 'left' && (
        <Icon
          as={props.isSelected ? props.icons.selected || LuCheck : props.icons.default || LuPlus}
          opacity={0.5}
        />
      )}
      {props.icons && props.iconPlacement === 'top' && (
        <Icon
          as={props.isSelected ? props.icons.selected || LuCheck : props.icons.default || LuPlus}
          boxSize={'1.75em'}
          marginBottom={'0.5em'}
          opacity={props.isSelected ? 1 : 0.5}
        />
      )}
      {props.children || <span>{props.title || props.value}</span>}
      {props.icons && props.iconPlacement === 'bottom' && (
        <Icon
          as={props.isSelected ? props.icons.selected || LuCheck : props.icons.default || LuPlus}
          boxSize={'1.75em'}
          marginTop={'0.5em'}
          opacity={props.isSelected ? 1 : 0.5}
        />
      )}
      {props.icons && props.iconPlacement === 'right' && (
        <Icon
          as={props.isSelected ? props.icons.selected || LuCheck : props.icons.default || LuPlus}
          opacity={0.5}
        />
      )}
    </Button>
  )
}
